import { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertTriangle, CheckCircle2, Info, Flame, Home, Building2 } from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const TarifsRenoConfig = () => {
  const [tarifs, setTarifs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const charger = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/tarifs-reno`);
      const d = await r.json();
      setTarifs(d);
    } catch (e) {
      setError('Erreur chargement: ' + e.message);
    }
    setLoading(false);
  };

  useEffect(() => { charger(); }, []);

  const sauvegarder = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const r = await authFetch(`${API}/admin/tarifs-reno`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tarifs)
      });
      const d = await r.json();
      if (d.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(d.detail || 'Erreur sauvegarde');
      }
    } catch (e) {
      setError('Erreur sauvegarde: ' + e.message);
    }
    setSaving(false);
  };

  const updateTarifCEE = (cat, val) => {
    setTarifs(prev => ({
      ...prev,
      tarifs_cee_mwhc: { ...prev.tarifs_cee_mwhc, [cat]: parseFloat(val) || 0 }
    }));
  };

  const updateProduitMPR = (produitId, cat, val) => {
    setTarifs(prev => ({
      ...prev,
      produits: {
        ...prev.produits,
        [produitId]: {
          ...prev.produits[produitId],
          mpr: { ...prev.produits[produitId].mpr, [cat]: parseFloat(val) || 0 }
        }
      }
    }));
  };

  const updateProduitCEE = (produitId, field, val) => {
    setTarifs(prev => ({
      ...prev,
      produits: {
        ...prev.produits,
        [produitId]: {
          ...prev.produits[produitId],
          [field]: parseFloat(val) || 0
        }
      }
    }));
  };

  const updatePlafond = (zone, nb, cat, val) => {
    setTarifs(prev => ({
      ...prev,
      plafonds_mpr_2026: {
        ...prev.plafonds_mpr_2026,
        [zone]: {
          ...prev.plafonds_mpr_2026[zone],
          [nb]: {
            ...prev.plafonds_mpr_2026[zone][nb],
            [cat]: parseInt(val) || 0
          }
        }
      }
    }));
  };

  if (loading) return <div className="text-slate-400 p-4">Chargement des tarifs...</div>;
  if (!tarifs) return <div className="text-red-400 p-4">Erreur: impossible de charger les tarifs</div>;

  const categories = ['bleu', 'jaune', 'violet', 'rose'];
  const catColors = {
    bleu: 'text-blue-400', jaune: 'text-yellow-400',
    violet: 'text-violet-400', rose: 'text-pink-400'
  };

  return (
    <div className="space-y-6" data-testid="tarifs-reno-config">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-400" />
            Tarifs Rénovation Énergétique
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Paramètres du simulateur d'éligibilité (formulaire qualification lead).
            {tarifs.updated_at && <> — MAJ: {new Date(tarifs.updated_at).toLocaleString('fr-FR')}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={charger} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
            data-testid="tarifs-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={sauvegarder} disabled={saving}
            data-testid="tarifs-save-btn"
            className="px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors">
            <Save className="w-4 h-4" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {saved && (
        <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" />Tarifs sauvegardés avec succès
        </div>
      )}
      {error && (
        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-sm text-red-400">
          <AlertTriangle className="w-4 h-4" />{error}
        </div>
      )}

      {/* Section 1: Tarifs CEE */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <h3 className="text-sm font-bold text-emerald-400 mb-1">Tarifs CEE (€/MWhc)</h3>
        <p className="text-xs text-slate-500 mb-3">Valorisation interne par catégorie de ménage. Sert au calcul de la prime CEE estimée.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {categories.map(cat => (
            <div key={cat}>
              <label className={`block text-xs font-medium mb-1 capitalize ${catColors[cat]}`}>{cat}</label>
              <div className="flex items-center gap-1">
                <input type="number" step="0.5" value={tarifs.tarifs_cee_mwhc?.[cat] || ''}
                  onChange={e => updateTarifCEE(cat, e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                <span className="text-xs text-slate-500 whitespace-nowrap">€/MWhc</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 2: Montants MPR + CEE + Contraintes par produit */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <h3 className="text-sm font-bold text-blue-400 mb-1">Produits : Montants MaPrimeRénov' + CEE + Contraintes</h3>
        <p className="text-xs text-slate-500 mb-3">Source: ANAH 2026 — Parcours par geste. Volumes CEE: Fiches standardisées P6.</p>

        {/* Légende */}
        <div className="flex items-center gap-4 mb-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Home className="w-3 h-3 text-amber-400" /> Maison requise</span>
          <span className="flex items-center gap-1"><Building2 className="w-3 h-3 text-violet-400" /> Accord copro en appart</span>
          <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-400" /> Restriction spéciale</span>
        </div>

        <div className="space-y-2">
          {/* Header */}
          <div className="grid grid-cols-[1fr_65px_65px_65px_65px_90px_70px] gap-1.5 text-xs text-slate-400 font-medium border-b border-slate-700 pb-1">
            <span>Produit</span>
            {categories.map(c => <span key={c} className={`text-center capitalize ${catColors[c]}`}>{c}</span>)}
            <span className="text-center text-emerald-400">CEE vol.</span>
            <span className="text-center">Logement</span>
          </div>

          {/* Rows */}
          {Object.entries(tarifs.produits || {}).map(([id, produit]) => {
            const ceeField = produit.cee_mwhc != null ? 'cee_mwhc'
              : produit.cee_mwhc_m2 != null ? 'cee_mwhc_m2'
              : produit.cee_mwhc_fenetre != null ? 'cee_mwhc_fenetre' : null;
            const ceeUnit = ceeField === 'cee_mwhc_m2' ? '/m²' : ceeField === 'cee_mwhc_fenetre' ? '/fen' : 'MWhc';
            const isMaisonReq = produit.maison_requise;
            const isCopro = produit.copro_appart;
            const isExcluMonogeste = produit.exclue_monogeste_2026 || produit.ite_exclue_mpr;

            return (
              <div key={id} className={`grid grid-cols-[1fr_65px_65px_65px_65px_90px_70px] gap-1.5 items-center py-1 ${isExcluMonogeste ? 'bg-red-900/10 rounded' : ''}`}>
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-sm text-white truncate">{produit.label}</span>
                  {produit.mpr_unite && <span className="text-xs text-slate-600 shrink-0">({produit.mpr_unite})</span>}
                  {isExcluMonogeste && <AlertTriangle className="w-3 h-3 text-red-400 shrink-0" title="Exclu MPR monogeste 2026" />}
                </div>
                {categories.map(cat => (
                  <input key={cat} type="number" step="1" value={produit.mpr?.[cat] ?? ''}
                    onChange={e => updateProduitMPR(id, cat, e.target.value)}
                    className={`w-full px-1.5 py-1 bg-slate-900 border rounded text-white text-xs text-center ${
                      isExcluMonogeste && cat !== 'rose' ? 'border-red-500/30' : 'border-slate-600'
                    }`} />
                ))}
                <div className="flex items-center gap-1">
                  {ceeField ? (
                    <>
                      <input type="number" step="0.1" value={produit[ceeField] ?? ''}
                        onChange={e => updateProduitCEE(id, ceeField, e.target.value)}
                        className="w-14 px-1.5 py-1 bg-slate-900 border border-slate-600 rounded text-white text-xs text-center" />
                      <span className="text-xs text-slate-600">{ceeUnit}</span>
                    </>
                  ) : <span className="text-xs text-slate-600 text-center w-full">-</span>}
                </div>
                <div className="flex items-center justify-center gap-1">
                  {isMaisonReq && <Home className="w-3.5 h-3.5 text-amber-400" title="Maison individuelle requise" />}
                  {isCopro && <Building2 className="w-3.5 h-3.5 text-violet-400" title="Accord copropriété en appartement" />}
                  {!isMaisonReq && !isCopro && <span className="text-xs text-emerald-500">Tous</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 3: Plafonds RFR */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <h3 className="text-sm font-bold text-violet-400 mb-1">Plafonds de revenus MaPrimeRénov' 2026</h3>
        <p className="text-xs text-slate-500 mb-3">Source: ANAH / service-public.gouv.fr — RFR annuel max par catégorie et nombre de personnes.</p>
        {['province', 'idf'].map(zone => (
          <div key={zone} className="mb-4">
            <h4 className="text-xs font-bold text-slate-300 mb-2 uppercase">{zone === 'idf' ? 'Île-de-France' : 'Province / Outre-mer'}</h4>
            <div className="space-y-1">
              <div className="grid grid-cols-[80px_1fr_1fr_1fr] gap-2 text-xs text-slate-400 font-medium">
                <span>Pers.</span>
                {['bleu', 'jaune', 'violet'].map(c => <span key={c} className={`text-center capitalize ${catColors[c]}`}>{c}</span>)}
              </div>
              {['1', '2', '3', '4', '5'].map(nb => (
                <div key={nb} className="grid grid-cols-[80px_1fr_1fr_1fr] gap-2 items-center">
                  <span className="text-sm text-white">{nb} pers.</span>
                  {['bleu', 'jaune', 'violet'].map(cat => (
                    <input key={cat} type="number" step="1"
                      value={tarifs.plafonds_mpr_2026?.[zone]?.[nb]?.[cat] || ''}
                      onChange={e => updatePlafond(zone, nb, cat, e.target.value)}
                      className="w-full px-2 py-1 bg-slate-900 border border-slate-600 rounded text-white text-xs text-center" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Section 4: Conditions d'éligibilité (résumé lecture seule) */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <h3 className="text-sm font-bold text-amber-400 mb-2">Conditions d'éligibilité (règles automatiques)</h3>
        <p className="text-xs text-slate-500 mb-3">Ces règles sont appliquées automatiquement dans le simulateur. Non modifiables ici.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { rule: 'Propriétaire occupant obligatoire', detail: 'Locataire → bloqué pour tous les produits MPR' },
            { rule: 'Résidence principale obligatoire', detail: 'Résidence secondaire → bloqué pour tous les produits MPR' },
            { rule: 'Ancienneté logement +15 ans (métropole)', detail: 'Requis pour tous les gestes MPR par geste. Exception : +2 ans pour remplacement chaudière fioul (avec dépose cuve)' },
            { rule: 'Maison individuelle', detail: 'PAC Air/Eau, Poêle, Chaudière biomasse, Combles, Planchers, Chauffe-eau solaire' },
            { rule: 'Accord copropriété (appart)', detail: 'PAC Air/Air, Isolation murs ITE — possible mais avec accord copro' },
            { rule: 'Exclus MPR monogeste 2026', detail: 'ITE, ITI, Planchers bas, Chaudière biomasse → CEE uniquement (possible en rénovation d\'ampleur)' },
            { rule: 'PAC Air/Eau = sortie fossile', detail: 'Nécessite chauffage gaz ou fioul existant. Chauffage électrique = pas de MPR (CEE seul)' },
            { rule: 'Catégorie Rose exclue', detail: 'Ménages aux ressources supérieures (Rose) = pas de MPR par geste. CEE uniquement' }
          ].map((item, i) => (
            <div key={i} className="p-2 bg-slate-900/50 rounded-lg border border-slate-700/50">
              <div className="text-xs text-amber-300 font-medium">{item.rule}</div>
              <div className="text-xs text-slate-500 mt-0.5">{item.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 5: Sources */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <h3 className="text-sm font-bold text-slate-300 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4" />Sources des données
        </h3>
        <ul className="space-y-1">
          {tarifs.sources && Object.entries(tarifs.sources).map(([key, val]) => (
            <li key={key} className="text-xs text-slate-400 flex items-start gap-2">
              <span className="text-slate-500 font-medium capitalize shrink-0">{key.replace(/_/g, ' ')} :</span>
              <span>{val}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Section 6: Notes détaillées par produit */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <h3 className="text-sm font-bold text-slate-300 mb-2">Notes détaillées par produit</h3>
        <div className="space-y-1">
          {Object.entries(tarifs.produits || {}).map(([id, p]) => (
            <div key={id} className="text-xs flex gap-2 items-start">
              <span className="text-orange-400 font-medium w-48 shrink-0 flex items-center gap-1">
                {p.maison_requise && <Home className="w-3 h-3 text-amber-400" />}
                {p.copro_appart && <Building2 className="w-3 h-3 text-violet-400" />}
                {p.label}
              </span>
              <span className="text-slate-400">{p.note || '-'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TarifsRenoConfig;
